import {
    DomainInitPayload,
    OffersCommerceInfo,
} from '@sxm-de/dev-kits/web-sdk/types';
import { getDefaultBillingProfile } from '../helpers/get-default-billing-profile';
import { Invoice } from '../types/invoice';
import {
    getAccessToken,
    normalizeLangToLocaleForServiceCall,
} from '@sxm-de/dev-kits/web-sdk/utils';
import { getInvoice } from '../helpers/get-invoice';

interface GetInvoicesFnPayload {
    startDateTime: string;
    endDateTime: string;
}

export type GetInvoicesFn = (payload: GetInvoicesFnPayload) => Promise<{
    details: Invoice[];
    offersInfo: OffersCommerceInfo;
}>;

export function initGetInvoicesFn({
    dependencies,
    countryCodeDefault,
    locale,
}: DomainInitPayload): GetInvoicesFn {
    const { clientSdk, microservicesSdk } = dependencies;
    return async ({ startDateTime, endDateTime }: GetInvoicesFnPayload) => {
        try {
            const { id: billingProfileId } =
                await getDefaultBillingProfile(dependencies);
            const { items } = await clientSdk.commerce.billing.getInvoices(
                billingProfileId,
                {
                    startDateTime,
                    endDateTime,
                },
            );
            const details = await Promise.all(
                items.map((item: { id: string }) =>
                    getInvoice(
                        { billingProfileId, invoiceId: item.id },
                        dependencies,
                    ),
                ),
            );
            // Get offer ids needed to get offers info
            const offerIdsArray = []
                .concat(...details.map((detail: any) => detail.lines))
                ?.map((line: any) => line?.invoiceLine?.offerId)
                ?.filter((offerId) => !!offerId);
            // Use a Set to get only unique offer ids
            const offerIds = new Set(offerIdsArray);
            // Get offers info
            const request = {
                offerIds: [...offerIds],
                accessToken: getAccessToken(),
                locales: locale.supported.map((locale) =>
                    normalizeLangToLocaleForServiceCall(
                        locale,
                        countryCodeDefault,
                        undefined,
                    ),
                ),
            };
            const offersInfo =
                await microservicesSdk.domains.offers.getOffersCommerceInfo(
                    request,
                );

            // TODO: consider putting offers info content needed into details objects
            return {
                details,
                offersInfo,
            };
        } catch (error: any) {
            /**
             * TODO:
             * Log error to error logger (not analytics).
             */
            throw new Error('SYSTEM');
        }
    };
}
